import { Button, Flex, Group, Stack, useMantineColorScheme } from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, TextSerif } from "@/components/elements";
import { IconPointFilled } from "@/components/icons";
import { useGarageMarketCarIds } from "@/services/cars/get-garage-market-car-ids";
import { useMarketDecadeCars } from "@/services/market/get-market-decade-cars";
import { useMarketMakeCars } from "@/services/market/get-market-make-cars";

import type { DropdownItem } from "@/components/elements/Dropdown/types";
import type { MarketCar } from "@/types/market";
import type { FC } from "react";

type ModelComparatorItem = {
  label: string;
  value: string;
  color?: string;
};

type ModelComparatorProps = {
  defaultModel1Id?: string;
  defaultModel2Id?: string;
  decade?: string;
  makeId?: string;
  onCancel?: () => void;
  onCompare: (model1Id: string, model2Id: string) => void;
};

function getModelLabel(car: MarketCar) {
  const variant = car.variant && car.variant.trim() !== "" ? ` (${car.variant})` : "";
  return `${car.make_name} ${car.model_name}${variant}`;
}

function toDropdownItem({
  inGarage,
  label,
  color: itemColor,
  value,
  defaultColor,
}: ModelComparatorItem & { inGarage?: boolean; defaultColor: string }): DropdownItem {
  const color = itemColor || defaultColor;
  return {
    value,
    label,
    children: (
      <Flex gap="xs" wrap="nowrap" align="center">
        <span>{label}</span>
        {/* UI decorator that highlights the car models present in one of the user's garages */}
        {inGarage && (
          <IconPointFilled size={11} color={color} style={{ flexShrink: 0 }} aria-hidden />
        )}
      </Flex>
    ),
  } as unknown as DropdownItem;
}

export const ModelComparator: FC<ModelComparatorProps> = ({
  defaultModel1Id,
  defaultModel2Id,
  decade,
  makeId,
  onCancel,
  onCompare,
}) => {
  const { t } = useTranslation();
  const [model1Id, setModel1Id] = useState<string | null | undefined>(defaultModel1Id || null);
  const [model2Id, setModel2Id] = useState<string | null | undefined>(defaultModel2Id || null);
  const { marketCarIds } = useGarageMarketCarIds();

  const { data: makeCars } = useMarketMakeCars({
    makeId: makeId ?? "",
    page: 1,
    pageSize: 999999,
  });
  const { data: decadeCars } = useMarketDecadeCars({
    decade: decade ?? "",
    page: 1,
    pageSize: 999999,
  });

  const modelsData = useMemo<ModelComparatorItem[]>(
    () =>
      (makeId ? makeCars?.results : decadeCars?.results)?.map((car) => ({
        label: getModelLabel(car),
        value: car.id,
      })) ?? [],
    [decadeCars?.results, makeCars?.results, makeId]
  );

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const color = isDark ? "var(--mantine-color-primary-2)" : "var(--mantine-color-primary-6)";

  const dropdownData = useMemo(
    () =>
      modelsData.map((item) =>
        toDropdownItem({
          ...item,
          defaultColor: color,
          inGarage: marketCarIds.has(item.value),
        })
      ),
    [marketCarIds, modelsData, color]
  );

  function handleSubmit() {
    if (model1Id && model2Id) {
      onCompare(model1Id, model2Id);
    }
  }

  if (modelsData.length === 0) {
    return null;
  }

  return (
    <Stack>
      <TextSerif>
        {t("compare.model.comparator.title_intro")}
        <strong>{t("compare.model.comparator.title_highlight")}</strong>
        {t("compare.model.comparator.title_outro")}
      </TextSerif>
      <Dropdown
        data={dropdownData}
        value={model1Id}
        onChange={setModel1Id}
        label={t("compare.model.comparator.label1")}
        placeholder={t("compare.model.comparator.placeholder")}
        searchable
      />
      <Dropdown
        data={dropdownData}
        value={model2Id}
        onChange={setModel2Id}
        label={t("compare.model.comparator.label2")}
        placeholder={t("compare.model.comparator.placeholder")}
        searchable
      />
      <Group gap="md" pt="md">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} flex={1} aria-label={t("common.cancel")}>
            {t("common.cancel")}
          </Button>
        )}
        <Button
          disabled={!model1Id || !model2Id}
          onClick={handleSubmit}
          flex={1}
          aria-label={t("compare.model.comparator.submit")}
        >
          {t("compare.model.comparator.submit")}
        </Button>
      </Group>
    </Stack>
  );
};
